type BlurReason = 'combo' | 'focus' | 'hidden' | 'printscreen';

/** Set on <html> while protected images are blurred; value is the trigger reason. */
export const SCREENSHOT_BLUR_ATTR = 'data-screenshot-blur';

const MODIFIER_KEYS = ['Meta', 'Shift', 'Control', 'Alt', 'AltGraph'];

class ScreenshotBlur {
  private readonly doc: Document;
  private readonly isWindows: boolean;
  readonly listeners: [EventTarget, string, EventListener][];
  private reason: BlurReason | null = null;
  private suppressUntilFocus = false;

  constructor(private readonly win: Window) {
    this.doc = win.document;
    const nav = win.navigator as Navigator & { userAgentData?: { platform?: string } };
    this.isWindows = /win/i.test(nav.userAgentData?.platform ?? nav.platform);
    this.listeners = [
      [this.doc, 'keydown', (e) => this.onKeyDown(e as KeyboardEvent)],
      [this.doc, 'keyup', (e) => this.onKeyUp(e as KeyboardEvent)],
      [this.doc, 'visibilitychange', () => this.onVisibilityChange()],
      [this.doc, 'pointermove', (e) => this.onPointer(e as PointerEvent)],
      [this.doc, 'pointerdown', (e) => this.onPointer(e as PointerEvent)],
      [this.doc, 'click', (e) => this.onClick(e)],
      // Capture-phase window listeners also see every element blur/focus; only the window's own count.
      [this.win, 'blur', (e) => e.target === this.win && this.onWindowBlur()],
      [this.win, 'focus', (e) => e.target === this.win && (this.suppressUntilFocus = false)]
    ];
  }

  blur(next: BlurReason) {
    this.reason ??= next;
    this.doc.documentElement.setAttribute(SCREENSHOT_BLUR_ATTR, this.reason);
  }

  clear() {
    this.reason = null;
    this.doc.documentElement.removeAttribute(SCREENSHOT_BLUR_ATTR);
  }

  // Modifier keydowns reach the page before the OS consumes the final key: Cmd+Shift+3/4/5 and Win+Shift+S,
  // plus Alt+PrintScreen / Win+PrintScreen on Windows. Ctrl+Alt is AltGr character input, not a capture.
  private isCaptureCombo(event: KeyboardEvent): boolean {
    const meta = event.metaKey || event.key === 'Meta';
    const shift = event.shiftKey || event.key === 'Shift';
    const alt = (event.altKey || event.key === 'Alt') && !event.ctrlKey && event.key !== 'AltGraph';
    return (meta && shift) || (this.isWindows && (meta || alt));
  }

  // Option is part of ordinary macOS typing, so it only holds the blur on Windows.
  private modifiersHeld(event: KeyboardEvent | PointerEvent): boolean {
    return event.metaKey || event.shiftKey || (this.isWindows && event.altKey);
  }

  private onKeyDown(event: KeyboardEvent) {
    if (this.isCaptureCombo(event)) {
      this.blur('combo');
    } else if (this.reason && this.reason !== 'combo' && !MODIFIER_KEYS.includes(event.key)) {
      this.clear();
    }
  }

  private onKeyUp(event: KeyboardEvent) {
    if (event.key === 'PrintScreen') {
      this.blur('printscreen');
    } else if (this.reason === 'combo' && !this.modifiersHeld(event) && this.doc.hasFocus()) {
      this.clear();
    }
  }

  // Deferred a tick: focus moving into an iframe (Braintree, PayPal) also blurs the window, and Safari updates activeElement late.
  private onWindowBlur() {
    if (this.suppressUntilFocus) {
      return;
    }
    this.win.setTimeout(() => {
      if (this.doc.activeElement?.tagName !== 'IFRAME') {
        this.blur('focus');
      }
    });
  }

  private onVisibilityChange() {
    if (this.doc.visibilityState === 'hidden') {
      this.blur('hidden');
    }
  }

  // OS capture overlays sit above the browser, so page pointer events mean the user is back. Windows needs a click.
  private onPointer(event: PointerEvent) {
    if (!this.reason || !this.doc.hasFocus()) {
      return;
    }
    // A combo blur whose keyup went to a snip overlay: modifiers released means the capture is over.
    if (this.reason === 'combo' && this.modifiersHeld(event)) {
      return;
    }
    if (event.type === 'pointerdown' || !this.isWindows) {
      this.clear();
    }
  }

  // The native file picker blurs the window; don't treat it as a capture.
  private onClick(event: Event) {
    if (event.target instanceof HTMLInputElement && event.target.type === 'file') {
      this.suppressUntilFocus = true;
    }
  }
}

// Deterrent only: blurs protected images around OS screenshot shortcuts (desktop). Third-party capture tools bypass it.
export function installScreenshotBlur(win: Window): () => void {
  if (!win.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    return () => {};
  }

  const blur = new ScreenshotBlur(win);
  blur.listeners.forEach(([target, type, handler]) => target.addEventListener(type, handler, true));

  return () => {
    blur.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler, true));
    blur.clear();
  };
}
